import * as XLSX from "xlsx";
import { DiameterGroup, ManholeRateItem } from "./rateCalculations";
import { Manhole, SewerPipeRun } from "../types/tender.types";

export interface ExcelExportOptions {
  projectName?: string;
  tenderRef?: string;
  exportDate?: Date;
}

/**
 * Export manhole and pipework rates to Excel format
 */
export const exportManholeRatesToExcel = (
  diameterGroups: DiameterGroup[],
  manholes: Manhole[],
  options: ExcelExportOptions = {},
  pipeworkData?: any[],
  pipeRuns?: SewerPipeRun[]
): void => {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Prepare data for the main rates sheet
    const worksheetData: any[][] = [];

    // Calculate totals
    const manholeTotal = diameterGroups.reduce(
      (sum, group) => sum + group.subtotal,
      0
    );
    const pipeworkTotal = pipeworkData
      ? pipeworkData.reduce((sum, item) => sum + item.amount, 0)
      : 0;
    const grandTotal = manholeTotal + pipeworkTotal;

    // Add header information
    worksheetData.push(["Manhole & Pipework Rates Schedule"]);
    worksheetData.push([""]);

    if (options.projectName) {
      worksheetData.push(["Project:", options.projectName]);
    }
    if (options.tenderRef) {
      worksheetData.push(["Tender Reference:", options.tenderRef]);
    }
    worksheetData.push([
      "Export Date:",
      (options.exportDate || new Date()).toLocaleDateString("en-GB"),
    ]);
    worksheetData.push(["Total Manholes:", manholes.length]);
    if (pipeRuns) {
      worksheetData.push(["Total Pipe Runs:", pipeRuns.length]);
    }
    worksheetData.push(["Manhole Total:", `£${manholeTotal.toFixed(2)}`]);
    if (pipeworkData && pipeworkData.length > 0) {
      worksheetData.push(["Pipework Total:", `£${pipeworkTotal.toFixed(2)}`]);
    }
    worksheetData.push(["Grand Total:", `£${grandTotal.toFixed(2)}`]);
    worksheetData.push([""]);

    // Add table headers
    worksheetData.push([
      "Item",
      "Description",
      "Qty",
      "Unit",
      "Rate (£)",
      "Amount (£)",
    ]);

    // Add manholes section
    worksheetData.push([
      "",
      "MANHOLES",
      "",
      "",
      "",
      `£${manholeTotal.toFixed(2)}`,
    ]);
    worksheetData.push([""]);

    // Add data for each manhole diameter group
    diameterGroups.forEach((group) => {
      // Add diameter header
      worksheetData.push([
        "",
        `${group.diameter} Diameter Manholes`,
        "",
        "",
        "",
        group.subtotal > 0 ? `£${group.subtotal.toFixed(2)}` : "",
      ]);

      // Add items for this diameter
      group.items.forEach((item) => {
        worksheetData.push([
          item.item,
          item.description,
          item.qty > 0 ? item.qty : "",
          item.unit,
          `£${item.rate.toFixed(2)}`,
          item.amount > 0 ? `£${item.amount.toFixed(2)}` : "",
        ]);
      });

      // Add spacing between groups
      worksheetData.push([""]);
    });

    // Add pipework section if data exists
    if (pipeworkData && pipeworkData.length > 0) {
      worksheetData.push([
        "",
        "PIPEWORK",
        "",
        "",
        "",
        `£${pipeworkTotal.toFixed(2)}`,
      ]);
      worksheetData.push([""]);

      // Group pipework data by diameter
      const pipeworkGroups = pipeworkData.reduce((acc, item) => {
        if (!acc[item.diameter]) {
          acc[item.diameter] = [];
        }
        acc[item.diameter].push(item);
        return acc;
      }, {} as Record<string, any[]>);

      Object.entries(pipeworkGroups).forEach(([diameter, items]) => {
        const subtotal = items.reduce((sum, item) => sum + item.amount, 0);

        // Add diameter header
        worksheetData.push([
          "",
          `${diameter} Diameter Pipework`,
          "",
          "",
          "",
          `£${subtotal.toFixed(2)}`,
        ]);

        // Add items for this diameter
        items.forEach((item) => {
          worksheetData.push([
            item.item,
            item.description,
            item.qty.toFixed(2),
            item.unit,
            `£${item.rate.toFixed(2)}`,
            `£${item.amount.toFixed(2)}`,
          ]);
        });

        // Add spacing between groups
        worksheetData.push([""]);
      });
    }

    // Add grand total
    worksheetData.push([
      "",
      "",
      "",
      "",
      "GRAND TOTAL:",
      `£${grandTotal.toFixed(2)}`,
    ]);

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    const columnWidths = [
      { wch: 8 }, // Item
      { wch: 40 }, // Description
      { wch: 8 }, // Qty
      { wch: 8 }, // Unit
      { wch: 12 }, // Rate
      { wch: 15 }, // Amount
    ];
    worksheet["!cols"] = columnWidths;

    // Style the header rows
    const headerRowIndex =
      worksheetData.findIndex((row) => row[0] === "Item") + 1;

    // Add the main rates worksheet
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rates Schedule");

    // Create a summary sheet
    const summaryData: any[][] = [];
    summaryData.push(["Manhole & Pipework Rates Summary"]);
    summaryData.push([""]);
    summaryData.push(["Category", "Diameter", "Count", "Total Value (£)"]);

    // Add manhole summaries
    diameterGroups.forEach((group) => {
      const count = group.items.reduce((sum, item) => sum + item.qty, 0);
      if (count > 0) {
        summaryData.push([
          "Manholes",
          group.diameter,
          count,
          `£${group.subtotal.toFixed(2)}`,
        ]);
      }
    });

    // Add pipework summaries if data exists
    if (pipeworkData && pipeworkData.length > 0) {
      const pipeworkGroups = pipeworkData.reduce((acc, item) => {
        if (!acc[item.diameter]) {
          acc[item.diameter] = [];
        }
        acc[item.diameter].push(item);
        return acc;
      }, {} as Record<string, any[]>);

      Object.entries(pipeworkGroups).forEach(([diameter, items]) => {
        const count = items.length;
        const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
        summaryData.push([
          "Pipework",
          diameter,
          count,
          `£${subtotal.toFixed(2)}`,
        ]);
      });
    }

    summaryData.push([""]);
    summaryData.push([
      "",
      "Total Manholes:",
      manholes.length,
      `£${manholeTotal.toFixed(2)}`,
    ]);
    if (pipeRuns) {
      summaryData.push([
        "",
        "Total Pipe Runs:",
        pipeRuns.length,
        `£${pipeworkTotal.toFixed(2)}`,
      ]);
    }
    summaryData.push(["", "GRAND TOTAL:", "", `£${grandTotal.toFixed(2)}`]);

    const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
    summaryWorksheet["!cols"] = [
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 15 },
    ];
    XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Summary");

    // Create detailed manholes sheet
    const manholeDetailData: any[][] = [];
    manholeDetailData.push(["Manhole Details"]);
    manholeDetailData.push([""]);
    manholeDetailData.push([
      "Reference",
      "Diameter",
      "Depth (m)",
      "Depth Category (m)",
      "Type",
    ]);

    manholes.forEach((manhole) => {
      manholeDetailData.push([
        manhole.ref,
        manhole.diameter,
        manhole.depth.toFixed(2),
        manhole.depthCategory.toFixed(2),
        manhole.type || "Standard",
      ]);
    });

    const manholeDetailWorksheet = XLSX.utils.aoa_to_sheet(manholeDetailData);
    manholeDetailWorksheet["!cols"] = [
      { wch: 12 }, // Reference
      { wch: 12 }, // Diameter
      { wch: 12 }, // Depth
      { wch: 15 }, // Depth Category
      { wch: 15 }, // Type
    ];
    XLSX.utils.book_append_sheet(
      workbook,
      manholeDetailWorksheet,
      "Manhole Details"
    );

    // Create pipework details sheet if pipework data exists
    if (pipeRuns && pipeRuns.length > 0) {
      const pipeworkDetailData: any[][] = [];
      pipeworkDetailData.push(["Pipework Details"]);
      pipeworkDetailData.push([""]);
      pipeworkDetailData.push([
        "From MH",
        "To MH",
        "Diameter",
        "Length (m)",
        "Avg Depth (m)",
        "Run Depth Category (m)",
        "Pipe Type",
      ]);

      pipeRuns.forEach((pipeRun) => {
        pipeworkDetailData.push([
          pipeRun.fromMH,
          pipeRun.toMH,
          pipeRun.diameter,
          pipeRun.length.toFixed(2),
          pipeRun.averageDepth.toFixed(2),
          pipeRun.runDepthCategory.toFixed(2),
          pipeRun.pipeType || "Standard",
        ]);
      });

      const pipeworkDetailWorksheet =
        XLSX.utils.aoa_to_sheet(pipeworkDetailData);
      pipeworkDetailWorksheet["!cols"] = [
        { wch: 10 }, // From MH
        { wch: 10 }, // To MH
        { wch: 12 }, // Diameter
        { wch: 12 }, // Length
        { wch: 12 }, // Avg Depth
        { wch: 18 }, // Run Depth Category
        { wch: 15 }, // Pipe Type
      ];
      XLSX.utils.book_append_sheet(
        workbook,
        pipeworkDetailWorksheet,
        "Pipework Details"
      );
    }

    // Generate filename
    const timestamp = new Date().toISOString().split("T")[0];
    const projectPrefix = options.projectName
      ? `${options.projectName.replace(/[^a-zA-Z0-9]/g, "_")}_`
      : "";
    const hasPipework = pipeworkData && pipeworkData.length > 0;
    const filename = hasPipework
      ? `${projectPrefix}Manhole_Pipework_Rates_${timestamp}.xlsx`
      : `${projectPrefix}Manhole_Rates_${timestamp}.xlsx`;

    // Export the file
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error("Error exporting manhole rates to Excel:", error);
    throw new Error("Failed to export manhole rates to Excel");
  }
};

/**
 * Export only the summary data for quick reference
 */
export const exportManholeRatesSummary = (
  diameterGroups: DiameterGroup[],
  manholes: Manhole[],
  options: ExcelExportOptions = {},
  pipeworkData?: any[],
  pipeRuns?: SewerPipeRun[]
): void => {
  try {
    const workbook = XLSX.utils.book_new();

    // Calculate totals
    const manholeTotal = diameterGroups.reduce(
      (sum, group) => sum + group.subtotal,
      0
    );
    const pipeworkTotal = pipeworkData
      ? pipeworkData.reduce((sum, item) => sum + item.amount, 0)
      : 0;
    const grandTotal = manholeTotal + pipeworkTotal;

    const summaryData: any[][] = [];
    summaryData.push(["Manhole & Pipework Rates Summary"]);
    summaryData.push([""]);

    if (options.projectName) {
      summaryData.push(["Project:", options.projectName]);
    }
    summaryData.push([
      "Export Date:",
      (options.exportDate || new Date()).toLocaleDateString("en-GB"),
    ]);
    summaryData.push([""]);

    summaryData.push(["Category", "Diameter", "Count", "Total Value (£)"]);

    let totalManholeCount = 0;

    // Add manhole summaries
    diameterGroups.forEach((group) => {
      const count = group.items.reduce((sum, item) => sum + item.qty, 0);
      if (count > 0) {
        summaryData.push([
          "Manholes",
          group.diameter,
          count,
          `£${group.subtotal.toFixed(2)}`,
        ]);
        totalManholeCount += count;
      }
    });

    // Add pipework summaries if data exists
    if (pipeworkData && pipeworkData.length > 0) {
      const pipeworkGroups = pipeworkData.reduce((acc, item) => {
        if (!acc[item.diameter]) {
          acc[item.diameter] = [];
        }
        acc[item.diameter].push(item);
        return acc;
      }, {} as Record<string, any[]>);

      Object.entries(pipeworkGroups).forEach(([diameter, items]) => {
        const count = items.length;
        const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
        summaryData.push([
          "Pipework",
          diameter,
          count,
          `£${subtotal.toFixed(2)}`,
        ]);
      });
    }

    summaryData.push([""]);
    summaryData.push([
      "",
      "Total Manholes:",
      totalManholeCount,
      `£${manholeTotal.toFixed(2)}`,
    ]);
    if (pipeRuns) {
      summaryData.push([
        "",
        "Total Pipe Runs:",
        pipeRuns.length,
        `£${pipeworkTotal.toFixed(2)}`,
      ]);
    }
    summaryData.push(["", "GRAND TOTAL:", "", `£${grandTotal.toFixed(2)}`]);

    const worksheet = XLSX.utils.aoa_to_sheet(summaryData);
    worksheet["!cols"] = [{ wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

    const timestamp = new Date().toISOString().split("T")[0];
    const projectPrefix = options.projectName
      ? `${options.projectName.replace(/[^a-zA-Z0-9]/g, "_")}_`
      : "";
    const hasPipework = pipeworkData && pipeworkData.length > 0;
    const filename = hasPipework
      ? `${projectPrefix}Manhole_Pipework_Rates_Summary_${timestamp}.xlsx`
      : `${projectPrefix}Manhole_Rates_Summary_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error("Error exporting manhole rates summary:", error);
    throw new Error("Failed to export manhole rates summary");
  }
};
